// pages/Home.jsx
import React, { useState, useEffect } from 'react';
import apiClient from '../config/api';
import Navbar from '../components/Navbar';
import FilterPanel from '../components/FilterPanel';
import FileTable from '../components/FileTable';
import StatusModal from '../components/StatusModal';
import DeleteModal from '../components/DeleteModal';
import DescriptionModal from '../components/DescriptionModal';
import HomeDocumentViewer from '../components/HomeDocumentViewer';
import { buildApiUrl } from '../config/api';
import { styles, modalStyles } from '../styles/newstyles';

const PAGE_SIZE = 50;

const parseDate = (value) => {
  if (!value || typeof value !== 'string') return 0;
  const isoLike = value.includes('T') ? value : value.replace(' ', 'T');
  const timestamp = Date.parse(isoLike);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const STATUS_SORT_ORDER = {
  1: 0, // New
  3: 1, // In progress
  4: 2, // Needs More Info
  2: 3 // Completed
};

const Home = ({ onLogout }) => {
  const [username, setUsername] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCA, setIsCA] = useState(false);

  // Everything related to filters/table list (copied from FileUploadForm, minus upload bits)
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [, setAllFiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allTypes, setAllTypes] = useState([]);
  const [counties, setCounties] = useState([]);

  const [filterCategory, setFilterCategory] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCounty, setFilterCounty] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterIsAdminOnly, setFilterIsAdminOnly] = useState('');

  const [filterTypes, setFilterTypes] = useState([]);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({ field: 'status', direction: 'asc' });

  const [showDescription, setShowDescription] = useState(null);
  const [showFilename, setShowFilename] = useState(null);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [forbiddenMessage, setForbiddenMessage] = useState('');

  const [statusModalFile, setStatusModalFile] = useState(null);
  const [newStatusId, setNewStatusId] = useState('');
  const [isSavingStatus] = useState(false);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [deleteEligibility, setDeleteEligibility] = useState({});
  const [isCheckingDeleteEligibility, setIsCheckingDeleteEligibility] = useState(false);
  const [bulkMessage, setBulkMessage] = useState('');
  const [previewFile, setPreviewFile] = useState(null);

  const TABLE_HEADER_COLOR = '#4A628A';

  useEffect(() => {
    apiClient.get('/auth/session', { withCredentials: true })
      .then(res => {
        if (res.data?.username) setUsername(res.data.username);
        if (res.data?.isAdmin) {
          setIsAdmin(true);
          apiClient.get('/lookup/counties')
            .then(r => setCounties(r.data))
            .catch(e => console.error('Failed to fetch counties', e));
        }
        if (res.data?.isCA) setIsCA(true);
      })
      .catch(err => console.error('Failed to fetch session:', err));

    apiClient.get('/lookup/categories')
      .then(r => setCategories(r.data))
      .catch(e => console.error('Failed to fetch categories', e));

    apiClient.get('/lookup/types')
      .then(r => setAllTypes(r.data))
      .catch(e => console.error('Failed to fetch all types', e));

    setIsTableLoading(true);
    handleApplyFilters(1, { resetPage: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filterCategory) {
      apiClient.get(`/lookup/types/${filterCategory}`)
        .then(r => setFilterTypes(r.data))
        .catch(e => { console.error('Failed to fetch filter types:', e); setFilterTypes([]); });
    } else {
      setFilterTypes([]);
    }
  }, [filterCategory]);

  const formatDate = (dateTimeString) => {
    if (typeof dateTimeString !== 'string') return 'N/A';
    const [datePart, timePartRaw] = dateTimeString.trim().split(' ');
    if (!datePart || !timePartRaw) return 'N/A';

    const [year, month, day] = datePart.split('-').map(v => parseInt(v, 10));
    const [hourRaw = '0', minuteRaw = '0', secondRaw = '0'] = timePartRaw.split(':');

    if ([year, month, day].some(Number.isNaN)) return 'N/A';

    const hour24 = parseInt(hourRaw, 10);
    const minute = parseInt(minuteRaw, 10) || 0;
    const second = parseInt(secondRaw, 10) || 0;
    if (Number.isNaN(hour24)) return 'N/A';

    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = ((hour24 + 11) % 12) + 1;
    const pad = (val) => String(val).padStart(2, '0');

    return `${month}/${day}/${String(year).slice(-2)} ${hour12}:${pad(minute)}:${pad(second)} ${ampm}`;
  };

  const getCategoryName = id => categories.find(c => c.ID === id)?.Name || id;
  const getTypeName = id => allTypes.find(t => t.ID === id)?.Name || id;
  const getSortValue = (file, field) => {
    switch (field) {
      case 'fileId':
        return Number(file?.FileID) || 0;
      case 'date':
        return parseDate(file?.UploadDate);
      case 'user':
        return (file?.UploadedBy || '').toString().toLowerCase();
      case 'category':
        return getCategoryName(file?.AttachmentCategoryID)?.toString().toLowerCase();
      case 'type':
        return getTypeName(file?.AttachmentTypeID)?.toString().toLowerCase();
      case 'county':
        return (file?.County || '').toString().toLowerCase();
      case 'status':
      default:
        return STATUS_SORT_ORDER[file?.StatusID] ?? Number.MAX_SAFE_INTEGER;
    }
  };

  const applySortToFiles = (files, field = sortConfig.field, direction = sortConfig.direction) => {
    const normalized = Array.isArray(files) ? [...files] : [];
    const sortField = field || 'status';
    const dirMultiplier = direction === 'desc' ? -1 : 1;

    return normalized.sort((a, b) => {
      const valueA = getSortValue(a, sortField);
      const valueB = getSortValue(b, sortField);

      if (valueA === valueB) {
        const uploadDiff = parseDate(b?.UploadDate) - parseDate(a?.UploadDate);
        if (uploadDiff !== 0) return uploadDiff;
        return (b?.FileID ?? 0) - (a?.FileID ?? 0);
      }

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return (valueA - valueB) * dirMultiplier;
      }

      const textA = (valueA || '').toString();
      const textB = (valueB || '').toString();
      if (textA === textB) return 0;
      return textA > textB ? 1 * dirMultiplier : -1 * dirMultiplier;
    });
  };

  const buildFilterParams = (overrides = {}) => {
    const category = overrides.filterCategory ?? filterCategory;
    const type = overrides.filterType ?? filterType;
    const county = overrides.filterCounty ?? filterCounty;
    const status = overrides.filterStatus ?? filterStatus;
    const isAdminOnlyFilter = overrides.filterIsAdminOnly ?? filterIsAdminOnly;

    const params = {
      pageSize: PAGE_SIZE,
      categoryId: category || null,
      typeId: type || null,
      statusId: status || null
    };
    if (isAdmin && county) params.countyId = county;
    if (isAdmin && isAdminOnlyFilter !== '') params.filterIsAdminOnly = isAdminOnlyFilter;
    return params;
  };

  const fetchAllFiles = async (params) => {
    const all = [];
    const firstResponse = await apiClient.get('/files/uploads', {
      params: { ...params, page: 1 },
      withCredentials: true
    });

    if (Array.isArray(firstResponse.data?.files)) {
      all.push(...firstResponse.data.files);
    }

    const totalPagesFromApi = firstResponse.data?.totalPages || 1;
    if (totalPagesFromApi > 1) {
      const requests = [];
      for (let p = 2; p <= totalPagesFromApi; p++) {
        requests.push(
          apiClient.get('/files/uploads', {
            params: { ...params, page: p },
            withCredentials: true
          })
        );
      }

      const responses = await Promise.all(requests);
      responses.forEach(res => {
        if (Array.isArray(res.data?.files)) all.push(...res.data.files);
      });
    }

    return all;
  };

  const loadAndPaginate = async ({
    page = 1,
    sortField = sortConfig.field,
    sortDirection = sortConfig.direction,
    resetPage = false,
    filters: filterOverrides = {}
  } = {}) => {
    setIsLoadingPage(true);
    setForbiddenMessage('');
    try {
      const params = buildFilterParams(filterOverrides);
      const files = await fetchAllFiles(params);
      const sorted = applySortToFiles(files, sortField, sortDirection);
      setSortConfig({ field: sortField, direction: sortDirection });
      setAllFiles(sorted);
      setSelectedFileIds(prev => {
        if (!isBulkMode) return [];
        const validIds = new Set(sorted.map(f => f.FileID));
        return prev.filter(id => validIds.has(id));
      });

      const computedTotalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
      const targetPage = resetPage ? 1 : Math.min(page, computedTotalPages);
      const start = (targetPage - 1) * PAGE_SIZE;
      const end = start + PAGE_SIZE;

      setTotalPages(computedTotalPages);
      setCurrentPage(targetPage);
      setUploadedFiles(sorted.slice(start, end));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        setUploadedFiles([]);
        setTotalPages(1);
        setCurrentPage(1);
        setForbiddenMessage('Access denied for restricted documents.');
        // Do not spam console for expected access control
        return;
      }
      console.error('Error loading files:', e);
    } finally {
      setIsLoadingPage(false);
      setIsTableLoading(false);
    }
  };

  const confirmDelete = (fileId) => {
    setFileToDelete(fileId);
    setDeleteReason('');
    setShowDeleteConfirm(true);
  };

  const handleConfirmedDelete = async () => {
    setIsDeleting(true);
    try {
      const reasonToSend = deleteReason && deleteReason.trim() ? deleteReason.trim() : null;
      const res = await apiClient.delete(`/files/uploads/${fileToDelete}`, {
        withCredentials: true,
        data: { reason: reasonToSend }
      });
      if (res.data?.success) {
        setAllFiles(prev => applySortToFiles(prev.filter(f => f.FileID !== fileToDelete)));
        handleRefresh();
        setDeleteMessage('File deleted successfully.');
      } else {
        setDeleteMessage('Unexpected error occurred.');
      }
    } catch (err) {
      const msg = err.response?.data?.error || (err.response?.status === 403 ? 'Unauthorized to delete this file.' : 'Delete failed. Please try again.');
      setDeleteMessage(msg);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setFileToDelete(null);
      setDeleteReason('');
      setTimeout(() => setDeleteMessage(''), 3000);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setIsBulkMode(false);
    setSelectedFileIds([]);
    setFilterCategory(''); setFilterType(''); setFilterCounty(''); setFilterStatus(''); setFilterIsAdminOnly('');
    await handleApplyFilters(1, { resetPage: true, filters: { filterCategory: '', filterType: '', filterCounty: '', filterStatus: '', filterIsAdminOnly: '' } });
    setIsRefreshing(false);
  };

  const handleApplyFilters = async (page = 1, options = {}) => {
    const { resetPage = false, sortField = sortConfig.field, sortDirection = sortConfig.direction, filters: filterOverrides = {} } = options || {};
    await loadAndPaginate({ page, sortField, sortDirection, resetPage, filters: filterOverrides });
  };

  const handleClearFilters = async () => {
    setFilterCategory(''); setFilterType(''); setFilterCounty(''); setFilterStatus(''); setFilterIsAdminOnly('');
    setIsBulkMode(false);
    setSelectedFileIds([]);
    setCurrentPage(1);
    await handleApplyFilters(1, { resetPage: true, filters: { filterCategory: '', filterType: '', filterCounty: '', filterStatus: '', filterIsAdminOnly: '' } });
  };

  const handleSort = (field) => {
    setSortConfig(prev => {
      const direction = prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc';
      const nextConfig = { field, direction };
      handleApplyFilters(1, { resetPage: true, sortField: nextConfig.field, sortDirection: nextConfig.direction });
      return nextConfig;
    });
  };

  const loadDeleteEligibility = async () => {
    setIsCheckingDeleteEligibility(true);

    const newFiles = uploadedFiles.filter(file => Number(file.StatusID) === 1);
    const eligibilityEntries = await Promise.all(newFiles.map(async file => {
      try {
        const response = await apiClient.get(`/status/uploads/${file.FileID}/owners`, {
          withCredentials: true
        });
        return [file.FileID, !Array.isArray(response.data) || response.data.length === 0];
      } catch (error) {
        console.error(`Failed to check assigned owners for file ${file.FileID}:`, error);
        return [file.FileID, false];
      }
    }));

    const nextEligibility = {};
    uploadedFiles.forEach(file => { nextEligibility[file.FileID] = false; });
    eligibilityEntries.forEach(([fileId, eligible]) => { nextEligibility[fileId] = eligible; });
    setDeleteEligibility(nextEligibility);
    setIsCheckingDeleteEligibility(false);
  };

  const toggleBulkMode = async () => {
    if (isBulkMode) {
      setIsBulkMode(false);
      setSelectedFileIds([]);
      return;
    }
    setIsBulkMode(true);
    setSelectedFileIds([]);
    if (isAdmin) await loadDeleteEligibility();
  };

  const handleToggleSelectFile = (fileId) => {
    setSelectedFileIds(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleSelectAllCurrentPage = (selectablePageIds = uploadedFiles.map(f => f.FileID)) => {
    setSelectedFileIds(prev => {
      const pageIds = selectablePageIds;
      const allSelected = pageIds.every(id => prev.includes(id));
      if (allSelected) {
        return prev.filter(id => !pageIds.includes(id));
      }
      const merged = new Set([...prev, ...pageIds]);
      return Array.from(merged);
    });
  };

  const handleViewFile = (file) => {
    const fileType = String(file?.FileType || '').toLowerCase();
    const fileName = String(file?.FileName || '');
    const isPdf = fileType === 'application/pdf' || /\.pdf$/i.test(fileName);
    const isSupportedPreview = isPdf
      || fileType.startsWith('image/')
      || /\.(png|jpe?g|tiff?|xlsx?|eml|msg)$/i.test(fileName)
      || fileType.includes('spreadsheet')
      || fileType.includes('excel')
      || fileType === 'message/rfc822'
      || fileType === 'application/vnd.ms-outlook';

    if (isSupportedPreview) {
      setPreviewFile(file);
      return;
    }

    window.open(buildApiUrl(`files/uploads/${file.FileID}/download`), '_blank', 'noopener,noreferrer');
  };

  const handleBulkMarkComplete = async () => {
    if (!selectedFileIds.length) return;
    setIsBulkUpdating(true);
    try {
      await apiClient.patch('/status/uploads/status/bulk', {
        fileIds: selectedFileIds,
        statusId: 2
      }, { withCredentials: true });

      setBulkMessage(`Marked ${selectedFileIds.length} file(s) as Completed.`);
      await handleApplyFilters(currentPage, { resetPage: false });
      setSelectedFileIds([]);
      setIsBulkMode(false);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Bulk update failed. Please try again.';
      setBulkMessage(msg);
    } finally {
      setIsBulkUpdating(false);
      setTimeout(() => setBulkMessage(''), 3000);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedFileIds.length) return;
    const hasIneligibleSelection = selectedFileIds.some(id => deleteEligibility[id] !== true);
    if (hasIneligibleSelection) {
      setBulkMessage('Delete blocked: remove highlighted files from the selection first.');
      setTimeout(() => setBulkMessage(''), 4000);
      return;
    }
    if (!window.confirm(`Delete ${selectedFileIds.length} selected file(s)?`)) return;

    setIsBulkUpdating(true);
    const idsToDelete = [...selectedFileIds];
    try {
      const response = await apiClient.delete('/files/uploads/bulk', {
        withCredentials: true,
        data: { fileIds: idsToDelete, reason: 'Bulk delete' }
      });
      const deletedCount = response.data?.deletedCount ?? idsToDelete.length;
      setBulkMessage(`Deleted ${deletedCount} file(s) successfully.`);
      setSelectedFileIds([]);
      setIsBulkMode(false);
      await handleApplyFilters(currentPage, { resetPage: false });
    } catch (err) {
      const rejected = err?.response?.data?.rejected;
      const detail = Array.isArray(rejected) && rejected.length
        ? ` ${rejected.map(item => `File ${item.fileId}: ${item.reason}`).join('; ')}`
        : '';
      setBulkMessage(`${err?.response?.data?.error || 'Bulk delete failed.'}${detail}`);
    } finally {
      setIsBulkUpdating(false);
      setTimeout(() => setBulkMessage(''), 5000);
    }
  };

  const statusOptions = [
    { id: 1, label: 'New' },
    { id: 3, label: 'In Progress' },
    { id: 4, label: 'Need More Info' },
    { id: 2, label: 'Completed' }
  ];

  const bulkButtonStyle = {
    background: TABLE_HEADER_COLOR,
    color: '#fff',
    border: 'none',
    padding: '10px 18px',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px',
    boxShadow: '0 4px 12px rgba(0,119,182,0.25)',
    transition: 'opacity 0.2s ease'
  };

  const markCompleteButtonStyle = {
    ...bulkButtonStyle,
    background: 'linear-gradient(135deg, #1f9d55 0%, #0f7a32 100%)'
  };

  const bulkToggleStyle = {
    background: '#f5f7fb',
    color: TABLE_HEADER_COLOR,
    border: `1px solid ${TABLE_HEADER_COLOR}1F`,
    padding: '10px 12px',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '13px',
    boxShadow: '0 6px 14px rgba(0,0,0,0.08)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s ease'
  };

  return (
    <div style={styles.mainContainer}>
      <Navbar username={username} onLogout={onLogout} isAdmin={isAdmin} />
      <FilterPanel
        {...{
          isFilterExpanded, setIsFilterExpanded,
          filterCategory, setFilterCategory,
          filterType, setFilterType,
          filterCounty, setFilterCounty,
          filterStatus, setFilterStatus,
          filterIsAdminOnly, setFilterIsAdminOnly,
          isRefreshing, handleRefresh,
          handleApplyFilters, handleClearFilters,
          categories, filterTypes, counties, isAdmin, styles
        }}
      />
      {forbiddenMessage && (
        <div style={{ ...styles.error, marginTop: 10 }}>{forbiddenMessage}</div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          margin: '10px 0 14px',
          gap: '10px',
          flexWrap: 'wrap'
        }}
      >
        <button
          style={{
            ...bulkToggleStyle,
            background: isBulkMode ? '#e8eef9' : '#f7f9fc',
            opacity: (isLoadingPage || isRefreshing || isBulkUpdating || isCheckingDeleteEligibility) ? 0.65 : 1
          }}
          onClick={toggleBulkMode}
          disabled={isBulkMode || isLoadingPage || isRefreshing || isBulkUpdating || isCheckingDeleteEligibility}
        >
          <span style={{ fontSize: '13px', fontWeight: 700 }}>Click to bulk edit</span>
          <span style={{ fontSize: '12px', color: TABLE_HEADER_COLOR }}>{isBulkMode ? '▲' : '▼'}</span>
        </button>
        {isBulkMode && (
          <>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 14px',
              borderRadius: '999px',
              background: 'linear-gradient(135deg, rgba(33, 50, 80, 0.9) 0%, rgba(53, 80, 120, 0.9) 100%)',
              color: '#f8fbff',
              fontWeight: 700,
              letterSpacing: '0.25px',
              boxShadow: '0 10px 24px rgba(0,0,0,0.14)'
            }}>
              <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 0 6px rgba(34,197,94,0.15)' }} />
              {selectedFileIds.length} selected
            </div>
            <button
              style={{
                ...markCompleteButtonStyle,
                opacity: (selectedFileIds.length === 0 || isBulkUpdating) ? 0.6 : 1,
                cursor: (selectedFileIds.length === 0 || isBulkUpdating) ? 'not-allowed' : 'pointer',
                minWidth: 130
              }}
              onClick={handleBulkMarkComplete}
              disabled={selectedFileIds.length === 0 || isBulkUpdating}
            >
              {isBulkUpdating ? 'Marking...' : 'Mark Complete'}
            </button>
            {isAdmin && (
              <button
                style={{
                  ...bulkButtonStyle,
                  background: '#b42318',
                  opacity: (!selectedFileIds.length || selectedFileIds.some(id => deleteEligibility[id] !== true) || isBulkUpdating || isCheckingDeleteEligibility) ? 0.6 : 1,
                  cursor: (!selectedFileIds.length || selectedFileIds.some(id => deleteEligibility[id] !== true) || isBulkUpdating || isCheckingDeleteEligibility) ? 'not-allowed' : 'pointer',
                  minWidth: 130
                }}
                onClick={handleBulkDelete}
                disabled={!selectedFileIds.length || selectedFileIds.some(id => deleteEligibility[id] !== true) || isBulkUpdating || isCheckingDeleteEligibility}
                title={selectedFileIds.some(id => deleteEligibility[id] !== true)
                  ? 'Remove highlighted files from the selection before deleting'
                  : 'Delete selected files'}
              >
                {isBulkUpdating ? 'Deleting...' : 'Delete Selected'}
              </button>
            )}
            <button
              style={{ ...bulkButtonStyle, background: '#b42318', minWidth: 80 }}
              onClick={toggleBulkMode}
              disabled={isBulkUpdating}
            >
              Exit
            </button>
          </>
        )}
      </div>
      <FileTable
        {...{
          uploadedFiles, styles, formatDate,
          getCategoryName, getTypeName,
          setShowDescription, setShowFilename, confirmDelete,
          setStatusModalFile, setNewStatusId,
          totalPages, currentPage, handleApplyFilters,
          sortConfig, onSort: handleSort,
          isRefreshing, isLoadingPage, isTableLoading,
          isBulkMode, selectedFileIds,
          deleteEligibility, isCheckingDeleteEligibility,
          onToggleSelectFile: handleToggleSelectFile,
          onSelectAllCurrentPage: handleSelectAllCurrentPage,
          isBulkUpdating,
          onMarkViewed: (id) => {
            setUploadedFiles(prev => prev.map(f =>
              f.FileID === id && f.HasViewFlag
                ? { ...f, IsViewedByUser: true }
                : f
            ));
          },
          onViewFile: handleViewFile,
          isCA,
          isAdmin
        }}
      />
      {previewFile && (
        <HomeDocumentViewer
          file={previewFile}
          categoryName={getCategoryName(previewFile.AttachmentCategoryID)}
          typeName={getTypeName(previewFile.AttachmentTypeID)}
          onClose={() => setPreviewFile(null)}
        />
      )}
      <StatusModal
        {...{
          statusModalFile, newStatusId, setNewStatusId,
          statusOptions, setStatusModalFile,
          handleStatusUpdate: () => handleApplyFilters(currentPage),
          styles, isSavingStatus, isAdmin
        }}
      />
      <DeleteModal
        {...{
          showDeleteConfirm,
          handleConfirmedDelete,
          setShowDeleteConfirm,
          isDeleting,
          modalStyles,
          deleteReason,
          setDeleteReason
        }}
      />
      <DescriptionModal
        {...{ showDescription, setShowDescription, modalStyles }}
      />
      <DescriptionModal
        showDescription={showFilename}
        setShowDescription={setShowFilename}
        modalStyles={modalStyles}
        title="Filename"
        field="FileName"
      />
      {(bulkMessage || deleteMessage) && (
        <div style={styles.deleteNotification}>{bulkMessage || deleteMessage}</div>
      )}
    </div>
  );
};

export default Home;
